import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type DirectAction = {
  label: string
  onClick: () => void
}

type SuggestionAction = {
  label: string
  suggestions: string[]
  onSuggest: (s: string) => void
}

export interface EmptyStateProps {
  icon?: ReactNode
  title: string
  description: string
  action?: DirectAction | SuggestionAction
  className?: string
}

function isSuggestionAction(action: DirectAction | SuggestionAction): action is SuggestionAction {
  return 'suggestions' in action
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 px-8 py-12 text-center',
        className,
      )}
      role="status"
      aria-label={title}
    >
      {icon && (
        <div
          className="text-[color:var(--color-neutral-400)] text-4xl"
          aria-hidden="true"
        >
          {icon}
        </div>
      )}

      <div className="space-y-1.5">
        <h3 className="text-[15px] font-semibold text-[color:var(--color-neutral-700)]">
          {title}
        </h3>
        <p className="text-[13px] text-[color:var(--color-neutral-500)] leading-relaxed max-w-xs">
          {description}
        </p>
      </div>

      {action && (
        <div className="flex flex-col items-center gap-3 mt-1">
          {isSuggestionAction(action) ? (
            <>
              <p className="text-[12px] text-[color:var(--color-neutral-400)]">{action.label}</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {action.suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => action.onSuggest(s)}
                    className={cn(
                      // min-h-[44px] = iOS minimum tap target. Barbara is
                      // one-handed at the bedside; chips smaller than this
                      // get mis-tapped and erode trust.
                      'min-h-[44px] px-4 py-2 rounded-full text-[12px] font-medium',
                      'border border-[color:var(--color-neutral-200)]',
                      'bg-[color:var(--color-neutral-50)]',
                      'text-[color:var(--color-neutral-600)]',
                      'hover:bg-[color:var(--color-neutral-100)]',
                      'active:scale-95 transition-transform duration-100',
                    )}
                    type="button"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <Button
              variant="outline"
              onClick={action.onClick}
              // Default size (h-10 = 40px) bumped to min-h-[44px] for the
              // one-handed-at-bedside tap-target floor.
              className="min-h-[44px] text-[color:var(--color-primary-500)] border-[color:var(--color-primary-500)]"
            >
              {action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
